import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchEverything } from '../api';
import { QueryState } from './QueryState';
import { Icon } from './Icon';
import { useAccountDay } from '../accountDay';
import { searchContextText } from '../taskVocab';

/** How each kind is named when it heads a group. Falls back to the raw kind plus an "s". */
const KIND_PLURAL: Record<string, string> = {
  task: 'Tasks',
  note: 'Notes',
  project: 'Projects',
  goal: 'Goals',
  label: 'Labels',
  habit: 'Habits',
};

/** How many rows the panel shows before it says how much it is holding back. */
const SHOWN = 10;

/**
 * Ten results, with every kind that matched represented in them.
 *
 * `hits.slice(0, 10)` looks fair and is not: the server ranks the whole union together and tasks
 * are the most numerous thing in any account, so the kinds with fewest rows are structurally last
 * and therefore *always* cut. Measured on one account: `q=SA` returned 16 hits including 5 goals
 * and 4 labels, and the panel showed 10 tasks and nothing else. The work that taught search about
 * goals and labels landed on the server and was thrown away on arrival.
 *
 * So: one from each kind in turn, kinds ordered by where their best hit ranked, until the budget
 * is spent. The server's ranking still decides which kind leads and which row within a kind comes
 * first; all this decides is that no kind is squeezed out by a more populous one.
 */
function represented<T extends { kind: string }>(hits: readonly T[], budget: number): T[] {
  const byKind = new Map<string, T[]>();
  for (const hit of hits) byKind.set(hit.kind, [...(byKind.get(hit.kind) ?? []), hit]);
  const queues = [...byKind.values()];
  const out: T[] = [];
  while (out.length < budget && queues.some((q) => q.length > 0)) {
    for (const q of queues) {
      if (out.length >= budget) break;
      const next = q.shift();
      if (next) out.push(next);
    }
  }
  // Back into the server's order, so the panel reads as ranked rather than as round-robin.
  return hits.filter((h) => out.includes(h));
}

/**
 * The hits, gathered by kind, each keeping the position it had in the flat list.
 *
 * The index is what the arrow keys count and what `aria-activedescendant` names, so it must not be
 * re-derived per group — grouping is allowed to change where a row is drawn and nothing else.
 * Groups appear in the order their first hit did, so the server's ranking still decides which kind
 * leads.
 */
function groupByKind<T extends { kind: string }>(
  hits: readonly T[],
): { kind: string; plural: string; hits: { hit: T; index: number }[] }[] {
  const groups = new Map<string, { hit: T; index: number }[]>();
  hits.forEach((hit, index) => {
    groups.set(hit.kind, [...(groups.get(hit.kind) ?? []), { hit, index }]);
  });
  return [...groups].map(([kind, list]) => ({
    kind,
    plural: KIND_PLURAL[kind] ?? `${kind}s`,
    hits: list,
  }));
}

/**
 * The snippet, with the server's `<mark>` delimiters turned into real elements.
 *
 * `ts_headline` wraps the matched terms so a caller can see *why* a hit matched — which is most of
 * the value for an agent reading the API, and would otherwise render here as literal `<mark>` text.
 *
 * Split rather than `dangerouslySetInnerHTML`. `ts_headline` does not escape the source, so a note
 * body containing markup would arrive as markup; every segment below is a React text node, which
 * escapes by construction. The marker is a delimiter here, not HTML being trusted.
 */
function Highlighted({ text }: { text: string }) {
  const parts = text.split(/<mark>|<\/mark>/);
  return (
    <>
      {parts.map((part, i) =>
        // Odd indices are what sat between the tags, which is the matched run.
        i % 2 === 1 ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>,
      )}
    </>
  );
}

/**
 * One search across tasks and notes.
 *
 * `/api/search` and its client function have existed since Phase 6 with no caller: the task list
 * has its own filter, which searches tasks only and only while you are on that screen. So a note
 * written three months ago inside a project was findable only by remembering which project it
 * was in — which is the situation search exists to rescue you from.
 *
 * In the header rather than on a screen, because the whole point is that it works from wherever
 * you happen to be.
 *
 * Behind a magnifier since Phase 70. As an always-open field it was the widest thing in the
 * header — 152px of a 375px phone, which is what made every screen scroll sideways once the nav
 * stopped absorbing overflow in a scroll strip. `/` still opens and focuses it in one keystroke,
 * so the path a person actually uses is unchanged; what went is the field sitting open on every
 * screen waiting to be used on a few of them.
 */
export function GlobalSearch({
  onOpenTask,
  onOpenProjects,
  onOpenGoal,
  onOpenLabels,
  onOpenHabits,
}: {
  onOpenTask: (id: string) => void;
  /** A project hit carries its id, so the screen opens *that* project rather than the list. */
  onOpenProjects: (projectId?: string) => void;
  /** Where a goal hit goes. Optional so a caller without a goals screen still compiles. */
  onOpenGoal?: (id: string) => void;
  /** Where a label hit goes. Labels live on the Labels screen; everything else does not. */
  onOpenLabels?: () => void;
  /**
   * Where a habit hit goes, with the habit's id: a result that names a habit should open that
   * habit. There is no panel for a habit, so it is the screen or nothing.
   */
  onOpenHabits?: (id?: string) => void;
}) {
  const { today } = useAccountDay();
  const [query, setQuery] = useState('');
  /** The field itself. `open` below is the results list under it — the two are not the same. */
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const results = useQuery({
    queryKey: ['search', query],
    queryFn: () => searchEverything(query),
    // Two characters: one letter matches most of the database and the snippet would be noise.
    enabled: query.trim().length >= 2,
  });

  // Dismissal, matching the notification bell: outside pointer, or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (wrapper.current?.contains(e.target as Node)) return;
      setOpen(false);
      // Collapse only when there is nothing in it — otherwise clicking away loses a query you are
      // still in the middle of.
      if (query.trim() === '') setExpanded(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      setExpanded(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, query]);

  /**
   * `/` focuses the search, the way it does in most things with one.
   *
   * Ignored while a field already has focus, or typing a slash into a task title would jump the
   * caret out of it — which is the classic way this shortcut goes wrong.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active as HTMLElement | null)?.isContentEditable === true;
      if (typing) return;
      e.preventDefault();
      setExpanded(true);
      setOpen(true);
      // After the render that creates it: the input does not exist until `expanded` is true.
      requestAnimationFrame(() => input.current?.focus());
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const hits = results.data ?? [];
  const shown = represented(hits, SHOWN);

  /*
    Which hit the keyboard is on.

    Reset whenever the results change: after typing another letter the list underneath is a
    different list, and keeping the index would leave the highlight on whatever now happens to be
    third. `-1` means "no choice yet", so a bare Enter does nothing rather than opening a result
    nobody looked at.
  */
  const [active, setActive] = useState(-1);
  useEffect(() => setActive(-1), [query]);

  const openHit = (hit: (typeof hits)[number]) => {
    setOpen(false);
    setExpanded(false);
    setQuery('');
    /*
      Each kind goes where it lives.

      A task has a panel of its own and a goal has its own screen; the other three had one shared
      destination — the Labels screen — from when a project *was* a label and notes had nowhere
      else to be. So a label went somewhere right for the wrong reason, and a note and a project
      went somewhere wrong. Now: a project opens itself, a note opens the project that holds it,
      and only a label goes to Labels.
    */
    if (hit.kind === 'task') onOpenTask(hit.id);
    else if (hit.kind === 'goal' && onOpenGoal) onOpenGoal(hit.id);
    else if (hit.kind === 'project') onOpenProjects(hit.id);
    else if (hit.kind === 'label' && onOpenLabels) onOpenLabels();
    // A habit's home is the Habits screen; there is no panel for one.
    else if (hit.kind === 'habit' && onOpenHabits) onOpenHabits(hit.id);
    /*
      A note opens whatever it is a note *on*.

      A note can hang off a task, a goal or a project, and this routed every one of them by
      `projectId` — so a note about a task sent you to the Projects screen, which does not mention
      it. Task first, then goal, then project: the narrowest thing that actually holds it.

      A note attached to none of the three still has nowhere to go, which is a gap in the product
      rather than in this function, and is filed as one.
    */
    else if (hit.taskId) onOpenTask(hit.taskId);
    else if (hit.goalId && onOpenGoal) onOpenGoal(hit.goalId);
    else onOpenProjects(hit.projectId ?? undefined);
  };

  /**
   * Down, up, Enter — on the field, because that is where the caret is.
   *
   * The results were reachable only by pointer or by tabbing past the field into them. Typing then
   * reaching for the mouse is the whole cost of a search box, and `/` exists so the keyboard never
   * has to leave.
   */
  const onFieldKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (shown.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % shown.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (i <= 0 ? shown.length - 1 : i - 1));
    } else if (event.key === 'Enter') {
      // Enter with nothing chosen opens the first, which is what a person means by "search, then
      // go" — but only once there is something to go to.
      event.preventDefault();
      const hit = shown[active === -1 ? 0 : active];
      if (hit) openHit(hit);
    }
  };

  return (
    <div className="global-search" ref={wrapper}>
      {/*
        A panel, the same shell capture opens.

        Search used to expand *in place*, growing a field inside a nav that then had to make room
        for it — which is how the `/` badge ended up on its own line and the header grew to 54px
        whenever anybody searched. A panel anchored to the header cannot do that, and it has room
        for things the cramped field never could: results grouped by what they are, and the keys
        written down where you can see them.
      */}
      <button
        type="button"
        className="icon-button"
        aria-label="Search"
        aria-expanded={expanded}
        aria-keyshortcuts="/"
        title="Search — /"
        onClick={() => {
          setExpanded((v) => !v);
          requestAnimationFrame(() => input.current?.focus());
        }}
      >
        <Icon name="search" />
      </button>

      {expanded && (
        <div className="search-panel card">
          <div className="search-field">
            <input
              ref={input}
              type="search"
              className="global-search-input"
              value={query}
              placeholder="Search tasks and notes"
              aria-label="Search tasks and notes"
              role="combobox"
              aria-expanded={open && shown.length > 0}
              aria-controls="search-listbox"
              aria-activedescendant={active >= 0 ? `search-hit-${active}` : undefined}
              onFocus={() => setOpen(true)}
              onKeyDown={onFieldKey}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
            />
            <span className="key-hint" aria-hidden="true">
              <kbd>/</kbd>
            </span>
          </div>

          {open && query.trim().length >= 2 && (
            <div className="search-results" data-testid="search-results">
              {results.isPending && <p className="muted small">Searching…</p>}
              {/*
            A failed search is not an empty one. `hits` comes from `data ?? []`, so on failure
            this said "Nothing matches that" — a confident wrong answer to "does this task
            exist", and one you act on by giving up looking.
          */}
              <QueryState query={results} what="the results" className="muted small" />
              {!results.isPending && !results.isError && hits.length === 0 && (
                <p className="muted small">Nothing matches that.</p>
              )}
              {/*
            A listbox, because the field now drives it.

            With arrow keys moving a highlight the input became a combobox in fact, so it says so:
            `aria-activedescendant` points at the row the keyboard is on while focus stays in the
            field, which is how a screen reader follows a selection it cannot see. `aria-selected`
            on a bare `<li>` — which is what this had first — is not valid ARIA at all; the role has
            to be `option` for it to mean anything.
          */}
              {/*
            Grouped by what the thing is, which is what the extra room is for.

            The index stays global across the groups — it is what `aria-activedescendant` points at
            and what the arrow keys count — so grouping changes where a row is drawn and nothing
            about how it is chosen. Each `kind` keeps its own `<ul>` because a heading followed by
            a list is a list with a heading; one `<ul>` with headings inside it is not.
          */}
              <div id="search-listbox" role="listbox" aria-label="Search results">
                {groupByKind(shown).map((group) => (
                  <div key={group.kind}>
                    <div className="search-group-head">{group.plural}</div>
                    <ul className="plain">
                      {group.hits.map(({ hit, index }) => (
                        <li
                          key={`${hit.kind}:${hit.id}`}
                          id={`search-hit-${index}`}
                          role="option"
                          data-testid="search-hit"
                          data-kind={hit.kind}
                          className={index === active ? 'is-active' : ''}
                          aria-selected={index === active}
                        >
                          <button
                            className="link"
                            onMouseEnter={() => setActive(index)}
                            onClick={() => openHit(hit)}
                          >
                            {hit.title}
                          </button>
                          {/*
                            The discriminator first, then the prose.

                            Two tasks called "Write runbook" were the same row. `context` is
                            whatever tells them apart cheapest for that kind — a date, a state, a
                            cadence — and it goes before the snippet because it is the part you
                            are scanning for.
                          */}
                          {hit.context && (
                            <span className="pill search-context">
                              {searchContextText(hit, today)}
                            </span>
                          )}
                          {hit.snippet && (
                            <span className="subtext search-snippet">
                              <Highlighted text={hit.snippet} />
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {/*
                What is being held back, said out loud.

                The panel showed ten of however many and never mentioned the rest, so "not in the
                results" and "not in the account" looked identical — and giving up looking is what
                you do about the second. `aria-live` because the count changes as you type and a
                screen reader is otherwise told only about the rows.
              */}
              {hits.length > shown.length && (
                <p className="muted small search-more" aria-live="polite" data-testid="search-more">
                  Showing {shown.length} of {hits.length}. Keep typing to narrow it.
                </p>
              )}
              <div className="search-keys subtext" aria-hidden="true">
                <kbd>↑</kbd> <kbd>↓</kbd> to move · <kbd>⏎</kbd> to open · <kbd>esc</kbd> to close
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
